import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Clock, Volume2, VolumeX, Pencil, ChevronDown } from "lucide-react";
import DOMPurify from "dompurify";
import ThreadPanel from "@/components/ThreadPanel";
import ShareRow from "@/components/ShareRow";
import HelpCTA from "@/components/HelpCTA";
import useReadAloud from "@/hooks/useReadAloud";

const EASE = [0.76, 0, 0.24, 1];

const FadeIn = ({ children, delay = 0, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

const Subsection = ({ heading, body, list }) => (
    <div className="mt-8 md:mt-10">
        <h3 className="font-display text-xl md:text-2xl leading-snug tracking-[-0.01em] mb-3">
            {heading}
        </h3>
        {body?.map((p, i) => (
            <p
                key={i}
                className="font-body text-base md:text-lg leading-[1.7] text-[color:var(--charcoal)]/90 mb-4 max-w-[68ch]"
            >
                {p}
            </p>
        ))}
        {list && (
            <dl className="mt-4 space-y-4">
                {list.map((item, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-12 gap-4 border-t border-[color:var(--charcoal)]/15 pt-4"
                    >
                        <dt className="col-span-12 md:col-span-3 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                            {item.term}
                        </dt>
                        <dd className="col-span-12 md:col-span-9 font-body text-base md:text-lg leading-[1.65] text-[color:var(--charcoal)]/90">
                            {item.detail}
                        </dd>
                    </div>
                ))}
            </dl>
        )}
    </div>
);

const ArticleBody = ({ section }) => (
    <div className="mt-8 md:mt-12 max-w-3xl">
        {section.heading && (
            <h2 className="font-display font-medium text-2xl md:text-3xl leading-snug tracking-[-0.01em] mb-5">
                {section.heading}
            </h2>
        )}

        {section.epigraph && (
            <blockquote className="border-l-2 border-[color:var(--charcoal)] pl-6 py-2 mb-8 font-display italic text-lg md:text-xl text-[color:var(--charcoal)]/90 leading-snug max-w-[52ch]">
                “{section.epigraph}”
                {section.epigraphAttribution && (
                    <cite className="block mt-2 not-italic font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                        — {section.epigraphAttribution}
                    </cite>
                )}
            </blockquote>
        )}

        {section.body?.map((p, i) => (
            <p
                key={i}
                className="font-body text-base md:text-lg leading-[1.7] text-[color:var(--charcoal)]/90 mb-4 max-w-[68ch]"
            >
                {p}
            </p>
        ))}

        {section.pullQuote && (
            <blockquote className="my-8 border-l-2 border-[color:var(--charcoal)] pl-6 font-display italic text-xl md:text-2xl leading-snug max-w-[36ch]">
                “{section.pullQuote}”
            </blockquote>
        )}

        {section.list && (
            <dl className="mt-6 space-y-4">
                {section.list.map((item, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-12 gap-4 border-t border-[color:var(--charcoal)]/15 pt-4"
                    >
                        <dt className="col-span-12 md:col-span-3 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                            {item.term}
                        </dt>
                        <dd className="col-span-12 md:col-span-9 font-body text-base md:text-lg leading-[1.65] text-[color:var(--charcoal)]/90">
                            {item.detail}
                        </dd>
                    </div>
                ))}
            </dl>
        )}

        {section.subsections?.map((sub, i) => (
            <Subsection key={i} {...sub} />
        ))}
    </div>
);

export default function ArticleSection({ article, canEdit = false }) {
    const [expanded, setExpanded] = useState(false);
    const {
        slug,
        threadId,
        number,
        kicker,
        title,
        subtitle,
        readMinutes,
        pdfUrl,
        pdfFilename,
        sections,
        collapseAfterHeading,
        body_html: bodyHtml,
    } = article;

    // Auto-expand when the reader landed here from a deep-link reply email
    // (`?cid=<id>#article-<slug>`) so the ThreadPanel actually renders and
    // the comment can be scrolled into view.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const params = new URLSearchParams(window.location.search);
        if (!params.get("cid")) return;
        const hash = window.location.hash || "";
        if (hash === `#article-${slug}`) setExpanded(true);
    }, [slug]);

    // Auto-collapse long articles at ~20 lines of body content. We measure
    // rendered height after mount / on window resize (mobile line-wrap
    // differs from desktop). Threshold is derived from body line-height
    // (`--body-line-height`) so the cap is always exactly ~20 lines regardless
    // of viewport typography.
    // Deterministic collapse: cap the collapsed view to ~20 lines of body
    // content, using a word-count estimate (≈12 words per line at 68ch,
    // ≈240 words total for 20 lines). We slice the `sections` array at the
    // first section whose cumulative word count crosses the cap.
    const COLLAPSED_WORD_CAP = 240;
    const wordCount = (s) => {
        if (!s || typeof s !== "string") return 0;
        return s.trim().split(/\s+/).length;
    };
    const sectionWordCount = (s) => {
        let n = wordCount(s.heading || "");
        if (s.epigraph) n += wordCount(s.epigraph);
        if (Array.isArray(s.body)) n += s.body.reduce((a, p) => a + wordCount(p), 0);
        if (s.pullQuote) n += wordCount(s.pullQuote);
        if (Array.isArray(s.list))
            n += s.list.reduce(
                (a, it) => a + wordCount(it.term) + wordCount(it.detail),
                0,
            );
        if (Array.isArray(s.subsections))
            n += s.subsections.reduce(
                (a, sub) =>
                    a
                    + wordCount(sub.heading || "")
                    + (Array.isArray(sub.body)
                        ? sub.body.reduce((b, p) => b + wordCount(p), 0)
                        : 0)
                    + (Array.isArray(sub.list)
                        ? sub.list.reduce(
                            (b, it) => b + wordCount(it.term) + wordCount(it.detail),
                            0,
                        )
                        : 0),
                0,
            );
        return n;
    };

    const totalWords = Array.isArray(sections)
        ? sections.reduce((a, s) => a + sectionWordCount(s), 0)
        : 0;
    // For body_html articles, approximate word count from stripped HTML.
    const bodyHtmlWords = bodyHtml
        ? bodyHtml.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length
        : 0;

    const canCollapse = totalWords + bodyHtmlWords > COLLAPSED_WORD_CAP + 40;

    // Slice sections so accumulated words <= COLLAPSED_WORD_CAP.
    let sliceIndex = sections?.length || 0;
    if (canCollapse && Array.isArray(sections)) {
        // If the article specifies an explicit collapse point, honour it
        // verbatim: show everything up to and including the named section,
        // then cut. Falls back to the word-count heuristic below.
        if (collapseAfterHeading) {
            const idx = sections.findIndex(
                (s) => (s.heading || "").trim() === collapseAfterHeading.trim(),
            );
            if (idx >= 0) {
                sliceIndex = idx + 1;
            } else {
                let acc = 0;
                for (let i = 0; i < sections.length; i++) {
                    acc += sectionWordCount(sections[i]);
                    if (acc > COLLAPSED_WORD_CAP) {
                        sliceIndex = Math.max(1, i);
                        break;
                    }
                }
            }
        } else {
            let acc = 0;
            for (let i = 0; i < sections.length; i++) {
                acc += sectionWordCount(sections[i]);
                if (acc > COLLAPSED_WORD_CAP) {
                    sliceIndex = Math.max(1, i);
                    break;
                }
            }
        }
    }
    const visibleSections =
        canCollapse && !expanded
            ? sections?.slice(0, sliceIndex)
            : sections;

    // Build a single readable string of the whole article for TTS.
    const readableText = (() => {
        const parts = [`Article ${number}. ${title}.`];
        if (subtitle) parts.push(subtitle);
        if (bodyHtml) {
            // Strip HTML tags for TTS
            const stripped = bodyHtml
                .replace(/<[^>]+>/g, " ")
                .replace(/&nbsp;/g, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/\s+/g, " ")
                .trim();
            parts.push(stripped);
        } else if (sections) {
            for (const s of sections) {
                if (s.heading) parts.push(s.heading);
                if (s.epigraph) parts.push(`Quote: ${s.epigraph}.`);
                if (s.body) for (const p of s.body) parts.push(p);
                if (s.pullQuote) parts.push(`Pull quote: ${s.pullQuote}.`);
                if (s.list)
                    for (const it of s.list) parts.push(`${it.term}: ${it.detail}`);
                if (s.subsections)
                    for (const sub of s.subsections) {
                        if (sub.heading) parts.push(sub.heading);
                        if (sub.body) for (const p of sub.body) parts.push(p);
                        if (sub.list)
                            for (const it of sub.list)
                                parts.push(`${it.term}: ${it.detail}`);
                    }
            }
        }
        return parts.filter(Boolean).join(" ");
    })();

    const { active, toggle, supported } = useReadAloud(
        `article-${slug}`,
        readableText,
    );

    return (
        <article
            id={`article-${slug}`}
            data-testid={`article-${slug}`}
            className="relative mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24 border-t border-[color:var(--charcoal)]/15 first:border-t-0"
        >
            <FadeIn>
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-8">
                        <div className="flex items-center gap-3 mb-4 flex-wrap">
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.28em] text-[color:var(--charcoal)]">
                                Article · {number}
                            </span>
                            <span className="inline-block h-px w-8 bg-[color:var(--charcoal)]/50" />
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                                {kicker}
                            </span>
                            {readMinutes &&
                                (supported ? (
                                    <button
                                        type="button"
                                        onClick={toggle}
                                        data-testid={`article-${slug}-read-aloud`}
                                        aria-pressed={active}
                                        aria-label={
                                            active
                                                ? "Stop reading this article aloud"
                                                : "Read this article aloud"
                                        }
                                        title={
                                            active
                                                ? "Stop reading aloud"
                                                : "Read aloud"
                                        }
                                        className="inline-flex items-center gap-1.5 font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors underline underline-offset-4 decoration-dotted"
                                    >
                                        {active ? (
                                            <>
                                                <VolumeX
                                                    size={11}
                                                    strokeWidth={1.7}
                                                />
                                                Stop reading
                                                <span
                                                    aria-hidden
                                                    className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--charcoal)] animate-pulse"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <Clock
                                                    size={11}
                                                    strokeWidth={1.6}
                                                />
                                                {readMinutes} min read
                                                <Volume2
                                                    size={11}
                                                    strokeWidth={1.7}
                                                    className="ml-1"
                                                />
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                                        <Clock size={11} strokeWidth={1.6} />
                                        {readMinutes} min read
                                    </span>
                                ))}
                        </div>
                        <h2 className="font-display font-medium text-[2rem] md:text-[3.375rem] lg:text-[4rem] leading-[0.95] tracking-[-0.02em]">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="mt-4 md:mt-5 font-body text-lg md:text-xl text-[color:var(--charcoal)]/85 max-w-[52ch] leading-snug">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    <div className="col-span-12 md:col-span-4 flex md:justify-end gap-2 flex-wrap">
                        {canEdit && (
                            <a
                                href={`/admin?edit=${slug}`}
                                data-testid={`article-${slug}-edit`}
                                title="Edit this article in the admin panel"
                                className="pill-btn ghost"
                                style={{ padding: "0.7rem 1rem" }}
                            >
                                <Pencil size={12} strokeWidth={1.7} />
                                Edit
                            </a>
                        )}
                        {pdfUrl ? (
                            <a
                                href={pdfUrl}
                                download={pdfFilename}
                                data-testid={`article-${slug}-download`}
                                className="pill-btn"
                                style={{ padding: "0.7rem 1.2rem" }}
                            >
                                <Download size={13} strokeWidth={1.6} />
                                Download PDF
                            </a>
                        ) : null}
                    </div>
                </div>
            </FadeIn>

            <FadeIn delay={0.05}>
                <div
                    id={`article-${slug}-body`}
                    data-testid={`article-${slug}-body`}
                    className="relative"
                >
                    <div>
                        <div>
                            {bodyHtml ? (
                                <div
                                    className="article-html mt-8 md:mt-12"
                                    data-testid={`article-${slug}-body-html`}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(bodyHtml, {
                                            USE_PROFILES: { html: true },
                                            ADD_TAGS: ["img"],
                                            ADD_ATTR: ["src", "alt", "title", "width", "height"],
                                        }),
                                    }}
                                />
                            ) : (
                                visibleSections?.map((s, i) => (
                                    <ArticleBody key={i} section={s} />
                                ))
                            )}
                        </div>
                    </div>

                    {canCollapse && !expanded && (
                        <div
                            className="relative"
                            data-testid={`article-${slug}-continue-wrap`}
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-x-0 -top-28 h-28 bg-gradient-to-b from-transparent to-[color:var(--cloud)]"
                            />
                            <div className="relative flex flex-col items-start gap-2 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setExpanded(true)}
                                    data-testid={`article-${slug}-continue`}
                                    aria-expanded="false"
                                    aria-controls={`article-${slug}-body`}
                                    className="pill-btn"
                                    style={{ padding: "0.7rem 1.25rem" }}
                                >
                                    <ChevronDown size={14} strokeWidth={1.7} />
                                    Continue reading
                                </button>
                                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                    {readMinutes ? `${readMinutes} min read total` : "Continue for full article"}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </FadeIn>

            {/* Article III (`en-concept`) closes the site's editorial flow, so
                instead of the usual Share/Download/Discussion tail it renders
                the standalone HELP CANADA CTA. Other articles keep the normal
                Share Row + Download strip + inline ThreadPanel below. */}
            {slug === "en-concept" ? (
                (!canCollapse || expanded) && <HelpCTA testId={`article-${slug}-help-cta`} />
            ) : (
                <>
                    {(!canCollapse || expanded) && (
                        <ShareRow
                            slug={slug}
                            title={title}
                            testIdPrefix={`article-${slug}-share`}
                        />
                    )}

                    {pdfUrl && (!canCollapse || expanded) && (
                        <div className="mt-14 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 border-t border-[color:var(--charcoal)]/20 pt-8">
                            <span className="font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                                End of article · {title}
                            </span>
                            <a
                                href={pdfUrl}
                                download={pdfFilename}
                                data-testid={`article-${slug}-download-bottom`}
                                className="pill-btn ghost"
                                style={{ padding: "0.55rem 1rem" }}
                            >
                                <Download size={13} strokeWidth={1.6} />
                                Download original PDF
                            </a>
                        </div>
                    )}

                    {(!canCollapse || expanded) && (
                        <ThreadPanel
                            threadId={threadId}
                            label={title}
                            testId={`thread-${slug}`}
                        />
                    )}
                </>
            )}
        </article>
    );
}
